import { Link } from "react-router-dom";

import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";

/**
 * Terms of Service.
 *
 * Copy rule: no em-dashes in body text. Commas, full stops, parens,
 * or line breaks.
 */
const Terms = () => (
  <>
    <PageMeta
      title="Terms of Service | Lembra"
      description="Terms governing your use of the Lembra workplace celebration platform, including orders, deliveries, payments, and dietary requirements."
      path="/terms"
    />
    <SkipToContent />
  <div className="min-h-screen bg-cream flex flex-col">
    <header className="py-6 px-6 md:px-10">
      <Logo size="md" />
    </header>
    <main id="main" className="flex-1 max-w-3xl mx-auto px-6 md:px-10 pb-16 w-full">
      <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">Terms of Service</h1>
      <p className="text-charcoal/60 text-sm mb-6">Last updated: 20 April 2026</p>

      <article className="prose prose-charcoal max-w-none space-y-6 text-charcoal leading-relaxed">
        <section>
          <h2 className="text-xl font-bold text-navy">1. Who we are</h2>
          <p>
            Lembra is operated by Lembra Pty Ltd, a company registered in Victoria, Australia, with its registered office at 11 Wilson Street, South Yarra VIC 3141. Throughout these terms, "Lembra," "we," "us," and "our" refer to Lembra Pty Ltd.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">2. Acceptance of these terms</h2>
          <p>
            By creating an account, placing an order, or otherwise using the Lembra platform (including the website at lembra.com.au), you agree to be bound by these Terms of Service and our <Link to="/privacy" className="text-coral underline">Privacy Policy</Link>. If you do not agree, do not use the platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">3. The service</h2>
          <p>
            Lembra helps Australian workplaces celebrate employee birthdays, work anniversaries, and other milestones by automating the coordination, ordering, and delivery of celebration gifts (cakes, hampers, flowers, vouchers, workshops, spa experiences, coffee, and related categories) through our network of independent vendor partners. We are a coordination platform. The gifts themselves are produced and supplied by our vendor partners.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">4. Your account</h2>
          <p>
            You must provide accurate and complete information when you register, keep your login credentials confidential, and notify us immediately of any unauthorised access. You are responsible for all activity that occurs under your account. You must be at least 18 years old and authorised to bind your organisation to contracts in order to use the platform on behalf of a business.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">5. Orders, payments, and pricing</h2>
          <p>
            All prices shown on the platform are in Australian Dollars and inclusive of GST unless otherwise stated. Payment is processed by our payment provider (Stripe). By placing an order, you authorise us to charge the payment method on file for the order total. Pricing and availability of individual vendor partners, gift options, and add-ons may change from time to time.
          </p>
          <p>
            Orders are confirmed once we have received payment and the vendor partner has accepted the order. An order is not binding on us until that confirmation is issued.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">6. Delivery</h2>
          <p>
            Delivery windows are indicative and depend on the partner vendor's operating hours, capacity, and category-specific lead time (cakes typically need 1–3 days; hampers, flowers, and spa vouchers vary; digital vouchers can be sent same-day). While we make every reasonable effort to deliver on time and to specification, late arrivals, product substitutions, or delivery failures caused by factors outside our reasonable control (traffic, weather, incorrect addresses provided by you) may occur. Please report any delivery issue to <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a> within 48 hours of the scheduled delivery so we can investigate.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">7. Dietary requirements and allergens</h2>
          <p>
            You are responsible for providing accurate dietary information (allergies, intolerances, religious or cultural requirements) for each recipient. For food-based gifts (cakes, hampers, and similar) our partner vendors handle common allergens including tree nuts, peanuts, wheat, eggs, dairy, and soy, and cross-contamination cannot be ruled out. If a recipient has a life-threatening allergy, please contact us before ordering to confirm whether a suitable product is available.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">8. Cancellations and refunds</h2>
          <p>
            Cancellation policies vary by how close the order is to the scheduled delivery date:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>More than 72 hours before delivery: full refund.</li>
            <li>Between 48 and 72 hours before delivery: 50% refund.</li>
            <li>Less than 48 hours before delivery: no refund, as the vendor has begun production or scheduling.</li>
          </ul>
          <p>
            Nothing in these terms limits the statutory guarantees you may be entitled to under the Australian Consumer Law. If a product is defective, unsafe, or substantially different from what was ordered, we will work with you to repair, replace, or refund the order as required under the ACL.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">9. Your responsibilities when using the platform</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Use the platform for any unlawful purpose or to facilitate fraud.</li>
            <li>Upload content (team member names, messages, dietary notes) that is offensive, discriminatory, or infringes third-party rights.</li>
            <li>Attempt to reverse-engineer, scrape, or probe the platform's systems without prior written consent.</li>
            <li>Share your account credentials or use the platform to impersonate another person or organisation.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">10. Intellectual property</h2>
          <p>
            The Lembra name, logo, platform, software, and associated materials are owned by Lembra Pty Ltd and protected by Australian and international intellectual property laws. You are granted a limited, non-exclusive, revocable licence to use the platform for its intended purpose. Content you upload (such as team member details) remains your property; by uploading it you grant us a limited licence to store and use it for the purpose of delivering the service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">11. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, our aggregate liability for any claim arising out of or relating to the platform is limited to the amount you paid us for the specific order to which the claim relates. We are not liable for indirect, incidental, consequential, or special damages, including loss of profits, goodwill, or data. This clause does not limit any rights or remedies you have under the Australian Consumer Law that cannot be excluded by contract.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">12. Suspension and termination</h2>
          <p>
            We may suspend or terminate your account if you materially breach these terms, fail to pay, or use the platform in a way that we reasonably believe poses a risk to us, our partners, or other users. You may close your account at any time by visiting Settings → Danger Zone or by emailing us.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">13. Changes to these terms</h2>
          <p>
            We may update these terms from time to time. Material changes will be communicated to you by email or through a notice on the platform at least 14 days before they take effect. Continued use of the platform after changes take effect constitutes acceptance of the updated terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">14. Governing law and disputes</h2>
          <p>
            These terms are governed by the laws of Victoria, Australia. The parties submit to the exclusive jurisdiction of the courts of Victoria. Before commencing any formal proceeding, both parties agree to attempt to resolve the dispute in good faith by contacting <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-navy">15. Contact</h2>
          <p>
            Lembra Pty Ltd<br />
            11 Wilson Street, South Yarra VIC 3141<br />
            <a href="mailto:support@lembra.com.au" className="text-coral underline">support@lembra.com.au</a>
          </p>
        </section>
      </article>

      <div className="mt-10">
        <Link to="/" className="text-sm text-coral hover:underline">← Back to home</Link>
      </div>
    </main>
  </div>
  </>
);

export default Terms;
